import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import FormDropdown from '../../components/form/FormDropdown';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';
import { identifierField, secretField } from '../../utils/validation-helper';

type TruvDisplayType = 'Dialog' | 'Inline';

const displayTypeOptions = [
  { label: 'Dialog (Popup)', value: 'Dialog' },
  { label: 'Inline (Embedded)', value: 'Inline' },
];

type TruvVoaFormValues = {
  templateId: string;
  clientId: string;
  accessKey: string;
  displayType: TruvDisplayType;
};

interface EditTruvVoaIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditTruvVoaIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditTruvVoaIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let templateId = '';
  let clientId = '';
  let accessKey = '';
  let displayType: TruvDisplayType = 'Inline';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    templateId = data.templateId || '';
    clientId = data.clientId || '';
    accessKey = data.accessKey || '';
    displayType = (data.displayType as TruvDisplayType) || 'Inline';
  }

  const validationSchema = yup.object().shape({
    templateId: identifierField('Template ID').required('Template ID is required'),
    clientId: identifierField('Client ID').required('Client ID is required'),
    accessKey: secretField('Access Key').required('Access Key is required'),
    displayType: yup.string().oneOf(['Dialog', 'Inline']).required(),
  });

  const { control, handleSubmit, formState } = useForm<TruvVoaFormValues>({
    defaultValues: {
      templateId: templateId,
      clientId: clientId,
      accessKey: accessKey,
      displayType: displayType,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: TruvVoaFormValues) => {

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            templateId: data.templateId,
            clientId: data.clientId,
            accessKey: data.accessKey,
            displayType: data.displayType,
          }),
        };
      } else {
        settings = {
          name: 'Truv VOA Integration',
          vendor: IntegrationVendor.Truv,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            templateId: data.templateId,
            clientId: data.clientId,
            accessKey: data.accessKey,
            displayType: data.displayType,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Truv VOA Settings' : 'Truv VOA Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="templateId"
            label="Template ID *"
            placeholder="Enter your Truv Template ID"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="clientId"
            label="Client ID *"
            placeholder="Enter your Truv Client ID"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="accessKey"
            label="Access Key *"
            placeholder="Enter your Truv Access Key"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormDropdown
            control={control}
            errors={errors}
            field="displayType"
            label="Display Type"
            placeholder="Select display type"
            options={displayTypeOptions}
            showClear={true}
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
